import { useEffect, useRef } from "react";

import Flicking from "@egjs/react-flicking";
import "@egjs/react-flicking/dist/flicking.css";
import { useParams, useHistory } from "react-router-dom";

import Box from "@/components/Box";
import CustomerPage from "@/components/CustomerPage";
import CustomerTabs from "@/components/CustomerTabs";
import IconType from "@/components/IconType";
import Loading from "@/components/Loading";
import InterpretationCard from "@/components/reports/InterpretationCard";
import InterpretationTabContent from "@/components/reports/InterpretationTabContent";
import ReportDetailNavigation from "@/components/reports/ReportDetailNavigation";
import CardListContext from "@/contexts/CardListContext";
import useWindowSize from "@/hooks/use-window-size";
import useCardList from "@/hooks/useCardList";
import Center from "@/tpo/Center";
import ChartModal from "@/tpo/ChartModal";
import Group from "@/tpo/Group";
import SegmentedControl from "@/tpo/SegmentedControl";
import Stack from "@/tpo/Stack";
import B2CHeader from "@/tpo/header/B2CHeader";
import { useQuery } from "@apollo/client";
import { useTheme } from "styled-components";

import ModelContent from "../testResults/ModelContent";
import {
  REPORT_DETAIL_QUERY,
  REPORT_RESULTS_QUERY,
  REPORTS_LIST_QUERY,
  USER_ENTITLEMENTS_QUERY,
  USER_RESULT_INTERPRETATION_DETAILS_QUERY
} from "./graphql/queries";
import OverviewTab from "./reportDetail/OverviewTab";
import CustomerRelatedTestsSection from "./reportDetail/RelatedTestsSection";

function CustomerOverviewTab(props) {
  const { reportSlug } = useParams();
  const printUrl = reportSlug ? `/reports/${reportSlug}/print` : undefined;

  return (
    <OverviewTab {...props} RelatedTestsSection={CustomerRelatedTestsSection} printUrl={printUrl} />
  );
}

export function ReportDetailContent({
  listQuery,
  detailQuery,
  listQueryVariables,
  skipListQuery,
  detailQueryVariables,
  getReportUrl,
  tabs,
  defaultActiveTab,
  UnlockedOverviewTabContent,
  SummaryTabContent,
  InterpretationTabContent,
  SuggestionsTabContent,
  interpretationQuery,
  interpretationQueryVariables,
  userEntitlements,
  interpretationListCard,
  interpretationListCardQuery,
  interpretationListCardQueryVariables
}) {
  const styledTheme = useTheme();
  const history = useHistory();
  const { reportSlug, tab: tabParam } = useParams();
  const flickingRef = useRef();
  const { width } = useWindowSize();
  const flickingAlign = width < 450 ? "0" : "center";

  const { data: reportsData, loading: listLoading } = useQuery(listQuery, {
    variables: {
      ...listQueryVariables
    },
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
    skip: skipListQuery
  });
  const reports = reportsData?.reports;

  const { data: detailData, loading: detailLoading } = useQuery(detailQuery, {
    variables: {
      subsectorSlug: reportSlug,
      ...detailQueryVariables
    },
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
    skip: !reportSlug
  });

  const userReports = reportsData?.userReports;

  const currentReport = detailData?.report;
  const currentUserReport = detailData?.userReport;
  const publishedViews = currentUserReport?.publishedViews || [];
  const computedTabs =
    tabs?.map(tab => ({
      ...tab,
      disabled: !publishedViews.includes(tab.value)
    })) || [];
  const fallbackTab =
    defaultActiveTab || computedTabs.find(tab => !tab.disabled)?.value || "overview";
  const validTabValues = computedTabs.filter(tab => !tab.disabled).map(tab => tab.value);
  const activeTab = validTabValues.includes(tabParam) ? tabParam : fallbackTab;

  useEffect(() => {
    if (!reportSlug) return;
    if (tabParam === activeTab) return;

    history.replace(getReportUrl(reportSlug, activeTab));
  }, [activeTab, getReportUrl, history, reportSlug, tabParam]);

  if (listLoading || detailLoading || !reports?.length) return <Loading />;

  if (!currentReport) return null;

  let tabContent = null;
  if (activeTab === "overview") {
    tabContent = (
      <UnlockedOverviewTabContent report={currentReport} userReport={currentUserReport} />
    );
  } else if (activeTab === "summary") {
    tabContent = <SummaryTabContent report={currentReport} />;
  } else if (activeTab === "interpretation") {
    tabContent = (
      <InterpretationTabContent
        report={currentReport}
        reportResultsQuery={interpretationQuery}
        reportResultsQueryVariables={interpretationQueryVariables}
        ListCard={interpretationListCard}
        listCardQuery={interpretationListCardQuery}
        listCardQueryVariables={interpretationListCardQueryVariables}
      />
    );
  } else if (activeTab === "suggestions") {
    tabContent = <SuggestionsTabContent report={currentReport} />;
  }

  return (
    <Group
      flexDirection={["column", "column", "row"]}
      justifyContent={["space-between", "space-between", "center"]}
      pt={styledTheme.spacing.section.pt}
      pb={styledTheme.spacing.section.pb}
      maxWidth={1648}
      mx="auto"
      gap={[20, 20, 40]}
    >
      <ReportDetailNavigation
        reports={userReports
          ?.filter(ur => {
            const grantedEntitlements = ur.subsector.purchaseProduct?.grantedEntitlements?.map(
              entitlement => entitlement.key
            );
            return userEntitlements
              ?.filter(entitlement => entitlement.granted)
              ?.some(userEntitlement => grantedEntitlements?.includes(userEntitlement.key));
          })
          ?.map(ur => ur.subsector)}
        currentSlug={reportSlug}
        getReportUrl={getReportUrl}
        activeTab={activeTab}
      />
      <Center stacked gap={[20, 20, 40]} maxWidth={1024} flexGrow={1} minWidth={0}>
        <Stack gap={20}>
          <Group gap={20} alignItems="center" flexDirection={["column", "column", "row"]}>
            <IconType
              iconType={currentReport?.purchaseProduct?.content?.reportIcon}
              iconSize={80}
              withCircle
            />
            <Box
              fontFamily="gilroyBold"
              fontSize={[40, 40, 68]}
              textAlign={["center", "center", "left"]}
            >
              {currentReport.name}
            </Box>
          </Group>
          {/* {currentReport.content.shortDescription && (
            <Box
              fontFamily="gilroyRegular"
              fontSize={[18, 18, 24]}
              lineHeight={["28px", "28px", "36px"]}
              maxWidth={720}
              mx="auto"
            >
              {currentReport.content.shortDescription}
            </Box>
          )} */}
        </Stack>
        <Box width="100%" minWidth={0} overflow="hidden">
          <Flicking
            align={flickingAlign}
            key={width}
            bound
            moveType="freeScroll"
            ref={flickingRef}
            style={{ paddingLeft: 20, paddingRight: 20 }}
          >
            <SegmentedControl
              data={computedTabs}
              value={activeTab}
              onChange={nextTab => {
                if (!reportSlug || nextTab === activeTab) return;
                history.push(getReportUrl(reportSlug, nextTab));
              }}
              bg="white"
              size={["xs", "xs", "sm"]}
            />
          </Flicking>
        </Box>
        <Box px={[16, 16, 0]} width="100%">
          {tabContent}
        </Box>
      </Center>
    </Group>
  );
}

export default function CustomerReportDetail() {
  const { reportSlug } = useParams();

  const { cardOpen, setCardOpen, viewCard } = useCardList();

  const TABS = [
    { value: "overview", label: "Overview" },
    { value: "summary", label: "Summary", disabled: true },
    { value: "interpretation", label: "Interpretation" },
    { value: "suggestions", label: "Suggestions", disabled: true },
    { value: "tests", label: "Tests", disabled: true }
  ];

  const { data: entitlementsData } = useQuery(USER_ENTITLEMENTS_QUERY, {
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first"
  });

  return (
    <>
      <B2CHeader />
      <CustomerPage
        bg="haze"
        includeFooter
        jumbotronProps={{
          title: "Reports"
        }}
      >
        <CustomerTabs selectedTab="/reports" />
        <CardListContext.Provider
          value={{
            viewCard
          }}
        >
          <ReportDetailContent
            listQuery={REPORTS_LIST_QUERY}
            detailQuery={REPORT_DETAIL_QUERY}
            detailQueryVariables={{
              subsectorSlug: reportSlug
            }}
            tabs={TABS}
            getReportUrl={(reportSlug, tab = "overview") => `/reports/${reportSlug}/${tab}`}
            UnlockedOverviewTabContent={CustomerOverviewTab}
            SummaryTabContent={() => null}
            InterpretationTabContent={InterpretationTabContent}
            interpretationQuery={REPORT_RESULTS_QUERY}
            interpretationQueryVariables={{
              subsectorSlug: reportSlug
            }}
            interpretationListCard={InterpretationCard}
            interpretationListCardQuery={USER_RESULT_INTERPRETATION_DETAILS_QUERY}
            interpretationListCardQueryVariables={{
              subsectorSlug: reportSlug
            }}
            SuggestionsTabContent={() => null}
            userEntitlements={entitlementsData?.user?.entitlements}
          />
          <ChartModal close={() => setCardOpen(undefined)} show={!!cardOpen?.type}>
            <ModelContent type={cardOpen?.type} item={cardOpen?.item} />
          </ChartModal>
        </CardListContext.Provider>
      </CustomerPage>
    </>
  );
}
